import { useQuery } from "@tanstack/react-query";
import { MitreAPI } from "../mitre.api";
import { SelectBox, useTheme } from "@monolith-forensics/monolith-ui";
import { Technique } from "../mitre.types";

export interface TechniquesSelectBoxProps {
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  /**
   * filter techniques by tactic
   */
  query?: { tactic?: string | null };
  disabled?: boolean;
}

export const TechniquesSelectBox: React.FC<TechniquesSelectBoxProps> = ({
  defaultValue,
  value,
  error,
  disabled,
  query,
  onChange,
}) => {
  const theme = useTheme();

  const { data: tactics } = useQuery({
    queryKey: ["mitre", "tactics", "list"],
    queryFn: () => MitreAPI.getTactics(),
    select: (data) => data?.data || [],
  });

  const { data: techniques, isLoading } = useQuery({
    queryKey: ["mitre", "techniques", "list", { pageSize: 1000 }],
    queryFn: () => MitreAPI.getTechniques({ pageSize: 1000 }),
    select: (data) =>
      (data?.data || []).map((technique) => ({
        label: technique.name,
        value: technique.uuid,
        data: technique,
      })),
  });

  const tactic = tactics?.find((tactic) => tactic.uuid === query?.tactic);

  const filteredTechniques =
    techniques?.filter((technique) => {
      if (tactic) {
        return technique.data.tactics
          .toLowerCase()
          .includes(tactic.name.toLowerCase());
      }
      return true;
    }) || [];

  return (
    <SelectBox
      data={filteredTechniques}
      defaultValue={defaultValue}
      value={value}
      label="MITRE Technique"
      placeholder="Select/Search a technique"
      onChange={onChange}
      loading={isLoading}
      searchable={true}
      error={error}
      disabled={disabled}
      renderOption={(option) => {
        let label = "";
        let data: Technique;
        if (typeof option === "string") {
          return option;
        } else {
          label = option.label;
          data = option.data;
        }
        return (
          <div style={{ overflow: "hidden" }}>
            <div
              title={label}
              style={{
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {label}
            </div>
            <div
              title={data.tactics}
              style={{
                fontSize: "0.8rem",
                color: theme.palette.text.secondary,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {data.tactics}
            </div>
          </div>
        );
      }}
    />
  );
};
